/* eslint-disable no-restricted-globals */
import { useState, useCallback, useContext, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Container from "react-bootstrap/Container";
import Modal from "react-bootstrap/Modal";
import Form from "react-bootstrap/Form";
import Button from "react-bootstrap/Button";
import Stack from "react-bootstrap/Stack";
import Alert from "react-bootstrap/Alert";
import Loading from "../../components/loading";
import { UserAuthenticatedContext } from "../../components/profile";
import { useCircleDarkMode } from "../../hooks/useCircleDarkMode";
import { useDebugMode } from "../../hooks/useDebugMode";
import { createWorkerFactory, useWorker } from "@shopify/react-web-worker";
import { usePMGradingTabs } from "../../hooks/usePMGradingTabs";
import { usePMGradingForm } from "../../hooks/usePMGradingForm";
import { usePMGradingConfig } from "../../hooks/usePMGradingConfig";
import { usePMGradingOperations } from "../../hooks/usePMGradingOperations";
import {
  createListingSku,
  flattenProductionData,
  formatDateAsHumanReadable,
} from "./utilities";
import PMGradingTabs from "./PMGradingTabs";
import AlertMessage from "./AlertMessage";
import ProductionForm from "./ProductionForm";
import PMUICatalogueInfo from "./catalogue-info";
import ProductDataViewerRequest, {
  ProductDataViewerChangeFormState,
} from "./product-data-viewer-request";
import { CreateItemLabelsHTMLProps } from "../../components/printing/generate-item-label-html";
import { RenderDebugInfo } from "./DebugInfo";
import { ProductionFormHeaderBar } from "./ProductionFormHeader";
import { CannotGradeAlert } from "./CannotGradeAlert";
import { PalletLabelPrint } from "../../components/pallet/pallet-label-print";
import { Printer } from "react-bootstrap-icons";
import { useSessionStorage } from "usehooks-ts";
import { updateCatalogueAndGetSKUs } from "../../workers/pmui";
import { Variant } from "react-bootstrap/esm/types";

// Create PM UI worker
const createPMUIWorker = createWorkerFactory(
  () => import("../../workers/pmui")
);

const allowedGradingStates = ["Refurb", "Quarantine"];
const PMGrading = () => {
  // Set up worker
  const pmuiWorker = useWorker(createPMUIWorker);

  // Authentication context
  const userAuthenticatedContext = useContext(UserAuthenticatedContext);

  // Dark mode and debug mode hooks
  const darkMode = useCircleDarkMode();
  const debugMode = useDebugMode();

  // State for UI
  const [newEntityId, setNewEntityId] = useState("");
  const [showCatalogueChangeModal, setShowCatalogueChangeModal] =
    useState(false);
  const [showPalletLabelPrintModal, setShowPalletLabelPrintModal] =
    useState(false);
  const [
    isRetrievingCatalogueItemForCurrentSKU,
    setIsRetrievingCatalogueItemForCurrentSKU,
  ] = useState(false);

  const [exitVariant, setExitVariant] = useState<Variant>("success");

  // State for tracking images per tab
  const [hasImagesByTab, setHasImagesByTab] = useState<{
    [key: string]: boolean;
  }>({});

  // Debug mode flag to override location status checks
  const [overrideLocationStatusBlock, setOverrideLocationStatusBlock] =
    useSessionStorage(`ccPMUIOverrideLocationStatusBlock`, false);

  // When printing labels, we normally save the form data first.
  // In debug mode, we can mock this behavior to avoid unnecessary saves.
  const [mockSaveBeforePrinting, setMockSaveBeforePrinting] = useSessionStorage(
    `ccPMUIMockSaveBeforePrinting`,
    process.env.REACT_APP_CCENV !== "prod" ? true : false
  );
  const [searchParams] = useSearchParams();

  // Custom hooks for state management
  const {
    tabs,
    activeTab: activePMID,
    pmRows,
    // Timestamps for data tracking (used for cache invalidation in future)
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    lastRetrieved,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    lastModified,
    loading: tabLoading,
    addTab,
    removeTab,
    switchTab,
    setTabLoading,
    updatePMRow: updateTabPMRow,
  } = usePMGradingTabs();

  const {
    // Used internally by the hook for storage
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    formDataByTab,
    simpleForm,
    initializeFormData,
    updateFormField,
    setActiveForm,
    clearFormData,
  } = usePMGradingForm();

  const {
    pmuiConfiguration,
    latestConfigurationRetrievalInProgress,
    packagingDescriptionOptions,
    disposalReasonOptions,
    engineers,
    faultCodeOptions,
    gradeOptions,
    returnReasonOptions,
    clientCodeOptions,
    fetchLatestConfiguration,
    forceFetchLatestConfiguration,
  } = usePMGradingConfig(
    pmuiWorker,
    userAuthenticatedContext.token,
    activePMID
  );

  const {
    currentCC2,
    loading: operationsLoading,
    alertState,
    showAlert,
    hideAlert,
    fetchPMRow,
    submitUpdatePMRow,
  } = usePMGradingOperations(
    pmuiWorker,
    userAuthenticatedContext.token,
    pmuiConfiguration,
    updateTabPMRow,
    initializeFormData
  );

  // If debug mode is changed to false, reset the overrideLocationStatusBlock
  useEffect(() => {
    if (!debugMode) {
      setOverrideLocationStatusBlock(false);
    }
  }, [debugMode, setOverrideLocationStatusBlock]);

  // Initialize a tab with data from the server
  const handleInitialAddTab = useCallback(
    async (entityId: string) => {
      if (!userAuthenticatedContext.token) {
        return;
      }

      try {
        addTab(entityId);
        setTabLoading(entityId, true);

        // Fetch the row data
        const rowData = await fetchPMRow(entityId);

        if (rowData) {
          // Save the row data
          updateTabPMRow(entityId, rowData);

          // Initialize the form data with production values
          const initialFormData = flattenProductionData(rowData);

          // Add a flag for disposal based on the disposal reason
          const withDisposalFlag = {
            ...initialFormData,
            disposal: !!initialFormData.disposalReason,
            hasImages: false, // Initialize as false, will be updated by iframe
          };

          initializeFormData(entityId, withDisposalFlag);

          // Set active form
          setActiveForm(entityId);
        }
      } finally {
        setTabLoading(entityId, false);
      }
    },
    [
      userAuthenticatedContext.token,
      addTab,
      fetchPMRow,
      setTabLoading,
      updateTabPMRow,
      initializeFormData,
      setActiveForm,
    ]
  );

  // Handle adding a new tab
  const handleAddNewTab = useCallback(() => {
    if (newEntityId && !tabs.includes(newEntityId)) {
      handleInitialAddTab(newEntityId);
      setNewEntityId("");
    } else if (tabs.includes(newEntityId)) {
      switchTab(newEntityId);
      setNewEntityId("");
    }
  }, [newEntityId, tabs, handleInitialAddTab, switchTab]);

  // Handle removing a tab
  const handleRemoveTab = useCallback(
    (tabId: string) => {
      removeTab(tabId);
      clearFormData(tabId);
      // Clean up image state for the removed tab
      setHasImagesByTab((prev) => {
        const { [tabId]: removed, ...rest } = prev;
        return rest;
      });
    },
    [removeTab, clearFormData]
  );

  // Handle tab switching
  const handleTabSwitch = useCallback(
    (tabId: string) => {
      if (tabId !== activePMID) {
        switchTab(tabId);
        setActiveForm(tabId);
      }
    },
    [activePMID, switchTab, setActiveForm]
  );

  // Save changes to the server
  const handleSaveChanges = useCallback(
    async (fromPrintOperation?: boolean) => {
      if (!activePMID || !userAuthenticatedContext.token) return false;

      // Check if BC grade requires images
      if (simpleForm.grade === "BC" && !hasImagesByTab[activePMID]) {
        showAlert(
          "Images are required for BC grade items. Please upload images before saving.",
          "warning",
          10000
        );
        return false;
      }

      if (fromPrintOperation && mockSaveBeforePrinting) {
        console.debug("Not doing a real save before printing labels");
        return true;
      }

      try {
        const rowData = pmRows[activePMID];
        if (!rowData) {
          throw new Error(`No data found for PMID: ${activePMID}`);
        }

        const success = await submitUpdatePMRow(
          activePMID,
          simpleForm,
          rowData
        );
        return success;
      } catch (error) {
        showAlert(
          `Failed to save changes: ${
            error instanceof Error ? error.message : "Unknown error"
          }`,
          "danger"
        );
        return false;
      }
    },
    [
      activePMID,
      userAuthenticatedContext.token,
      hasImagesByTab,
      simpleForm,
      showAlert,
      mockSaveBeforePrinting,
      pmRows,
      submitUpdatePMRow,
    ]
  );

  // Update form field
  const handleUpdateFormField = useCallback(
    (updater: (prev: any) => any) => {
      if (activePMID) {
        updateFormField(updater, activePMID);
      }
    },
    [activePMID, updateFormField]
  );

  // Helper function to generate effective PMID with updated client code
  const getEffectivePMID = useCallback(
    (pmid: string, clientCode?: string) => {
      if (!clientCode) return pmid;

      // Extract the suffix (everything after the last hyphen)
      const lastHyphenIndex = pmid.lastIndexOf("-");
      if (lastHyphenIndex === -1) return pmid;

      const suffix = pmid.substring(lastHyphenIndex);

      // Find the correct prefix from CC2 data based on client code
      const cc2Entry = pmuiConfiguration.cc2Data?.find(
        (cc2) => cc2.client === clientCode
      );

      const effectivePrefix = cc2Entry ? cc2Entry.skuSuffix : clientCode;
      return `${effectivePrefix}${suffix}`;
    },
    [pmuiConfiguration.cc2Data]
  );

  // Helper function to get effective client prefix for creating listing SKU
  const getEffectiveClientPrefix = useCallback(
    (pmid: string, clientCode?: string) => {
      if (!clientCode) {
        // Fallback to original logic if no client code override
        return pmid.substring(0, pmid.lastIndexOf("-"));
      }

      // If client code has changed, find the correct prefix from CC2 data
      const cc2Entry = pmuiConfiguration.cc2Data?.find(
        (cc2) => cc2.client === clientCode
      );

      if (cc2Entry) {
        return cc2Entry.skuSuffix;
      }

      // Fallback to the client code itself if no CC2 entry found
      return clientCode;
    },
    [pmuiConfiguration.cc2Data]
  );

  // Handle image state changes from iframe
  const handleImageStateChange = useCallback(
    (tabId: string, hasImages: boolean) => {
      setHasImagesByTab((prev) => ({
        ...prev,
        [tabId]: hasImages,
      }));

      // Also update the form field to keep it in sync
      if (tabId === activePMID) {
        updateFormField(
          (prev: any) => ({
            ...prev,
            hasImages: hasImages,
          }),
          tabId
        );
      }
    },
    [activePMID, updateFormField]
  );

  const handleGetUpdatedSkuInfo = useCallback(
    (sku: string) => {
      if (!currentCC2 || !userAuthenticatedContext.token) {
        return;
      }
      setIsRetrievingCatalogueItemForCurrentSKU(true);
      updateCatalogueAndGetSKUs(
        userAuthenticatedContext.token,
        currentCC2.pmSpreadsheetId,
        [sku]
      )
        .then((updateResult) => {
          console.info("received updated SKU info", updateResult);
          pmRows[activePMID].catalogue = updateResult.items[0];
        })
        .finally(() => {
          setIsRetrievingCatalogueItemForCurrentSKU(false);
        });
    },
    [currentCC2, userAuthenticatedContext.token, pmRows, activePMID]
  );

  // One-time initialization from URL on component mount
  const isInitialMount = useRef(true);

  // Initialize from URL params - only runs once on component mount
  useEffect(() => {
    if (isInitialMount.current) {
      const pmid = searchParams.get("pmid");

      // Only process pmid if it exists and is non-empty
      if (pmid) {
        if (!tabs.includes(pmid)) {
          // Add the tab if it's not already open
          handleInitialAddTab(pmid);
        } else {
          // If already open, just switch to it
          switchTab(pmid);
          const rowData = pmRows[pmid];
          if (rowData) {
            setActiveForm(pmid);
          } else {
            fetchPMRow(pmid).then((data) => {
              if (data) {
                updateTabPMRow(pmid, data);
                const formData = flattenProductionData(data);
                initializeFormData(pmid, {
                  ...formData,
                  disposal: !!formData.disposalReason,
                  hasImages: false, // Initialize as false, will be updated by iframe
                });
                setActiveForm(pmid);
              }
            });
          }
        }
      }
      isInitialMount.current = false;
    }
  }, [
    searchParams,
    tabs,
    handleInitialAddTab,
    pmRows,
    switchTab,
    fetchPMRow,
    updateTabPMRow,
    initializeFormData,
    setActiveForm,
  ]);

  // Load configuration if needed
  useEffect(() => {
    if (!userAuthenticatedContext.token) {
      return;
    }
    fetchLatestConfiguration();
    if (!currentCC2 && activePMID) {
      fetchPMRow(activePMID, true).then((data) => {
        if (data) {
          updateTabPMRow(activePMID, data);
          const formData = flattenProductionData(data);
          initializeFormData(activePMID, {
            ...formData,
            disposal: !!formData.disposalReason,
            hasImages: false, // Initialize as false, will be updated by iframe
          });
        }
      });
    }
  }, [
    userAuthenticatedContext.token,
    pmuiConfiguration,
    fetchLatestConfiguration,
    currentCC2,
    activePMID,
    fetchPMRow,
    updateTabPMRow,
    initializeFormData,
  ]);

  // Function to print item label
  const printItemLabel = () => {
    if (!activePMID || !pmRows[activePMID]) {
      return;
    }
    const currentItem = formDataByTab[activePMID];

    // Perform validation for printing
    if (
      !currentItem.refurbPalletNumber ||
      !currentItem.productionNumberOfBoxes ||
      !currentItem.doesProductMatchLink ||
      !currentItem.engineer
    ) {
      // Scroll to top and show alert if validation fails
      showAlert(
        "The following fields are required to print labels: Pallet Number, Number of Boxes, Product Match, and Engineer.",
        "warning",
        15000
      );
      return;
    }

    // Check if BC grade requires images
    if (currentItem.grade === "BC" && !hasImagesByTab[activePMID]) {
      showAlert(
        "Images are required for BC grade items. Please upload images before printing labels.",
        "warning",
        15000
      );
      return;
    }

    // Ensure form is saved before printing
    handleSaveChanges(true).then((saveResult) => {
      if (!saveResult) {
        showAlert("Failed to save changes before printing labels.", "danger");
        return;
      }

      // Use the effective client prefix (updated client code if changed)
      const effectiveClientPrefix = getEffectiveClientPrefix(
        activePMID,
        simpleForm.clientCode
      );

      const listingSku = createListingSku({
        catalogueExit: pmRows[activePMID].exit,
        clientPrefix: effectiveClientPrefix,
        grade: currentItem.grade ?? "",
        pmid: activePMID,
        sku: pmRows[activePMID].catalogue?.sku,
      });

      let comments = "";
      // Show listing comments on label only in certain conditions
      if (
        currentItem.grade?.startsWith("C") || // cover C1, C2 etc.
        currentItem.grade?.startsWith("BC") ||
        currentItem.grade?.includes("BER")
      ) {
        comments = currentItem.listingComments ?? "";
      }

      // Use effective PMID that reflects the client code change for the label
      const effectivePMID = getEffectivePMID(activePMID, simpleForm.clientCode);

      const data = btoa(
        encodeURIComponent(
          JSON.stringify({
            palletRef: currentItem.refurbPalletNumber || "",
            boxCount: currentItem.productionNumberOfBoxes || 1,
            pmid: effectivePMID, // Use the effective PMID instead of original
            packerName: currentItem.engineer,
            packedDateTime: formatDateAsHumanReadable(new Date()),
            sku: listingSku,
            productListingTitle:
              pmRows[activePMID].catalogue?.clientProductTitle,
            grade: currentItem.grade,
            comments,
          } as CreateItemLabelsHTMLProps)
        )
      );

      // Open new page with labels
      window.open(
        `/item-label-generator?dataEncoded=${data}`,
        "_blank",
        "noopener,noreferrer"
      );
    });
  };

  const [catalogueRequestFields, setCatalogueRequestFields] =
    useState<Partial<ProductDataViewerChangeFormState>>();

  useEffect(() => {
    setExitVariant((current: string) => {
      let variant: Variant = "danger";
      if (pmRows[activePMID]?.exit === "Retail") {
        variant = "success";
      } else if (pmRows[activePMID]?.exit === "Auction") {
        variant = "warning";
      }
      return variant;
    });
  }, [pmRows, activePMID]);

  useEffect(() => {
    if (!activePMID || !pmRows[activePMID]) {
      setCatalogueRequestFields(undefined);
      return;
    }
    const currentCatalogue = pmRows[activePMID].catalogue;
    const catalogueRequestFields: Partial<ProductDataViewerChangeFormState> = {
      boxes: currentCatalogue.boxes,
      boxW: currentCatalogue.boxW,
      boxH: currentCatalogue.boxH,
      boxL: currentCatalogue.boxL,
      kgs: currentCatalogue.totalKg,
      description: currentCatalogue.description,
      existingImageURLs: currentCatalogue.images || [],
      sku: currentCatalogue.sku,
      title: currentCatalogue.clientProductTitle,
      rrp: currentCatalogue.rrp,
      originatingPMID: activePMID,
    };

    setCatalogueRequestFields(catalogueRequestFields);
  }, [activePMID, pmRows]);

  // Loading state
  const isLoading =
    latestConfigurationRetrievalInProgress ||
    (activePMID && tabLoading[activePMID]) ||
    (activePMID && operationsLoading[activePMID]) ||
    !userAuthenticatedContext.token;

  // Render loading state
  if (isLoading && (!pmuiConfiguration.configuration || !activePMID)) {
    return <Loading />;
  }

  return (
    <Container fluid className={darkMode ? "bg-dark text-light" : ""}>
      {/* Alert message */}
      <AlertMessage
        show={alertState.show}
        message={alertState.message}
        variant={alertState.variant}
        onClose={hideAlert}
      />

      <h1 className="mb-4">PMUI Grading</h1>
      <Row>
        {/* Left column - navigation */}
        <Col md={3}>
          <Stack direction="vertical">
            <Button
              type="button"
              variant="info"
              onClick={() => setShowPalletLabelPrintModal(true)}
              size="lg"
            >
              <Printer size={20} /> Print Pallet Labels
            </Button>
            <br />
            <PMGradingTabs
              tabs={tabs}
              activeTab={activePMID}
              onTabSelect={handleTabSwitch}
              onTabRemove={handleRemoveTab}
              onAddNewTab={handleAddNewTab}
              newEntityId={newEntityId}
              setNewEntityId={setNewEntityId}
              loading={tabLoading}
            />

            {/* Debug information (if debug mode is enabled) */}
            {debugMode ? (
              <div className="mt-3">
                <div className="debug-header mb-2">
                  <Form.Check
                    type="switch"
                    id="mock-save-before-printing"
                    label="Mock Save before printing labels"
                    checked={mockSaveBeforePrinting}
                    onChange={() => setMockSaveBeforePrinting((v) => !v)}
                  />
                </div>
                <div className="debug-header mb-2">
                  <Form.Check
                    type="switch"
                    id="override-location-status-block"
                    label="Override Location Status Block (Debug)"
                    checked={overrideLocationStatusBlock}
                    onChange={() => setOverrideLocationStatusBlock((v) => !v)}
                  />
                </div>
                <h5 className="mb-3">Debug Information</h5>
                <RenderDebugInfo
                  activePMID={activePMID}
                  simpleForm={simpleForm}
                  pmRows={pmRows}
                  currentCC2={currentCC2}
                  engineers={engineers}
                  faultCodeOptions={faultCodeOptions}
                  returnReasonOptions={returnReasonOptions}
                  gradeOptions={gradeOptions}
                  packagingDescriptionOptions={packagingDescriptionOptions}
                  disposalReasonOptions={disposalReasonOptions}
                />
              </div>
            ) : null}
          </Stack>
        </Col>

        {/* Right column - form and metadata */}
        {activePMID && (
          <>
            {/* Form column */}
            <Col md={5}>
              {/* Sticky header with action buttons */}
              <ProductionFormHeaderBar
                darkMode={darkMode}
                lastRetrieved={lastRetrieved}
                activePMID={activePMID}
                lastModified={lastModified}
                printItemLabel={printItemLabel}
                isLoading={isLoading}
                formDataByTab={formDataByTab}
                getEffectivePMID={getEffectivePMID}
                fetchPMRow={async () => {
                  const fetchResult = await fetchPMRow(activePMID);
                  if (!fetchResult) {
                    return null;
                  }
                  updateTabPMRow(activePMID, fetchResult);
                  const formData = flattenProductionData(fetchResult);
                  initializeFormData(activePMID, {
                    ...formData,
                    disposal: !!formData.disposalReason,
                    hasImages: false, // Initialize as false, will be updated by iframe
                  });
                  return fetchResult;
                }}
                forceFetchLatestConfiguration={forceFetchLatestConfiguration}
                handleSaveChanges={handleSaveChanges}
                handleRemoveTab={handleRemoveTab}
                showPalletLabelPrintModal={setShowPalletLabelPrintModal}
              />
              {overrideLocationStatusBlock ||
              allowedGradingStates.includes(
                pmRows[activePMID]?.locationStatus
              ) ? (
                <ProductionForm
                  pmRow={pmRows[activePMID]}
                  formData={simpleForm}
                  updateFormField={handleUpdateFormField}
                  engineers={engineers}
                  faultCodeOptions={faultCodeOptions}
                  returnReasonOptions={returnReasonOptions}
                  gradeOptions={gradeOptions}
                  clientCodeOptions={clientCodeOptions}
                  packagingDescriptionOptions={packagingDescriptionOptions}
                  disposalReasonOptions={disposalReasonOptions}
                  activeTab={activePMID}
                  onSaveChanges={handleSaveChanges}
                  isLoading={isLoading}
                  currentCC2={currentCC2}
                  disabled={isLoading || !userAuthenticatedContext.token}
                  setShowPalletLabelPrintModal={setShowPalletLabelPrintModal}
                  hasImages={hasImagesByTab[activePMID] || false}
                  onImageStateChange={(hasImages) =>
                    handleImageStateChange(activePMID, hasImages)
                  }
                />
              ) : (
                <>
                  {isLoading ? (
                    <Loading />
                  ) : (
                    <>
                      <CannotGradeAlert
                        allowedGradingStatuses={allowedGradingStates}
                        currentLocationStatus={
                          pmRows[activePMID]?.locationStatus
                        }
                      />
                      <ProductionForm
                        pmRow={pmRows[activePMID]}
                        formData={simpleForm}
                        updateFormField={handleUpdateFormField}
                        engineers={engineers}
                        faultCodeOptions={faultCodeOptions}
                        returnReasonOptions={returnReasonOptions}
                        gradeOptions={gradeOptions}
                        clientCodeOptions={clientCodeOptions}
                        packagingDescriptionOptions={
                          packagingDescriptionOptions
                        }
                        disposalReasonOptions={disposalReasonOptions}
                        activeTab={activePMID}
                        onSaveChanges={handleSaveChanges}
                        isLoading={isLoading}
                        currentCC2={currentCC2}
                        disabled={true}
                        hideImageUpload={true}
                        setShowPalletLabelPrintModal={
                          setShowPalletLabelPrintModal
                        }
                        hasImages={hasImagesByTab[activePMID] || false}
                        onImageStateChange={(hasImages) =>
                          handleImageStateChange(activePMID, hasImages)
                        }
                      />
                    </>
                  )}
                </>
              )}
            </Col>
            {/* Catalogue info column */}
            <Col md={4}>
              {pmRows[activePMID] && (
                <>
                  {pmRows[activePMID]?.exit ? (
                    <Alert variant={exitVariant}>
                      <h2>
                        Exit:{" "}
                        {pmRows[activePMID]?.exit
                          ? pmRows[activePMID]?.exit
                          : "Unknown"}
                      </h2>
                    </Alert>
                  ) : null}

                  {/* Add Inbounding Information */}
                  {pmRows[activePMID]?.inbounding ? (
                    <>
                      <h4>Inbounding Info</h4>
                      <Row className="mb-1">
                        <Col>
                          <b>Date/Time</b>{" "}
                          {pmRows[activePMID].inbounding.dateTime ? (
                            <span>
                              {pmRows[activePMID].inbounding.dateTime}
                            </span>
                          ) : (
                            <span className="text-muted">No date present</span>
                          )}
                        </Col>
                      </Row>
                      <Row>
                        <Col>
                          <b>Notes</b>{" "}
                          {pmRows[activePMID].inbounding.notes ? (
                            <span>{pmRows[activePMID].inbounding.notes}</span>
                          ) : (
                            <span className="text-muted">No notes present</span>
                          )}
                        </Col>
                      </Row>
                    </>
                  ) : null}
                  <br />
                  <PMUICatalogueInfo
                    disabled={isLoading}
                    onChangeCatalogue={() => setShowCatalogueChangeModal(true)}
                    getLatestCatalogueForActiveSku={() =>
                      handleGetUpdatedSkuInfo(pmRows[activePMID].catalogue.sku)
                    }
                    isRetrievingCatalogueItemForCurrentSKU={
                      isRetrievingCatalogueItemForCurrentSKU
                    }
                    pmGradingRow={pmRows[activePMID]}
                    currentCC2={currentCC2}
                  />
                </>
              )}
            </Col>
          </>
        )}
      </Row>

      <Modal
        show={showCatalogueChangeModal}
        onHide={() => setShowCatalogueChangeModal(false)}
        backdrop="static"
        keyboard={false}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>Change Product Catalogue</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {currentCC2 ? (
            <ProductDataViewerRequest
              initialValues={catalogueRequestFields}
              originatingPMID={activePMID}
              currentPMSheetId={currentCC2.pmSpreadsheetId}
              onSubmitSuccess={() => {
                showAlert(
                  `Catalogue change request submitted successfully for SKU: ${catalogueRequestFields?.sku}`,
                  "success"
                );
                setTimeout(hideAlert, 3000);
                setShowCatalogueChangeModal(false);
              }}
            />
          ) : (
            <div className="text-center">
              <p className="text-muted">
                No CC2 data available for this PM
                <br />
                <Button
                  className="mt-2"
                  variant="success"
                  onClick={() => fetchPMRow(activePMID, true)}
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                        aria-hidden="true"
                      ></span>
                      Loading...
                    </>
                  ) : (
                    "Try and retrieve from PM"
                  )}
                </Button>
              </p>
            </div>
          )}
        </Modal.Body>
      </Modal>
      {/* Modal for printing pallet labels */}
      <Modal
        show={showPalletLabelPrintModal}
        onHide={() => setShowPalletLabelPrintModal(false)}
        backdrop="static"
        keyboard={false}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>Print Pallet Label</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <PalletLabelPrint
            closePalletLabelPrintModal={() =>
              setShowPalletLabelPrintModal(false)
            }
            pmSpreadsheetId={currentCC2?.pmSpreadsheetId}
            cc2Data={pmuiConfiguration.cc2Data}
            activePMID={activePMID}
            palletNumberInput={simpleForm.refurbPalletNumber}
            palletTypeInput={
              simpleForm.postProductionExit === "Retail"
                ? "R"
                : simpleForm.postProductionExit === "Auction"
                ? "A"
                : simpleForm.postProductionExit === "BTS"
                ? "B"
                : undefined
            }
            currentRowPalletInfo={
              activePMID && pmRows[activePMID] && pmRows[activePMID].catalogue
                ? {
                    clientId: simpleForm.clientCode ?? "",
                    comments: simpleForm.listingComments ?? "",
                    catalogInfo: {
                      clientItemId: pmRows[activePMID]?.catalogue?.sku ?? "",
                      name:
                        pmRows[activePMID]?.catalogue?.clientProductTitle ?? "",
                    },
                    legacyPMID: getEffectivePMID(
                      activePMID,
                      simpleForm.clientCode
                    ),
                  }
                : undefined
            }
          />
        </Modal.Body>
      </Modal>
    </Container>
  );
};

export default PMGrading;
